/**
 * Anonymous-scan access token storage. The token is what authorises the visitor's browser
 * to view its own scan, submit the email gate, and download the PDF. Two persistence
 * layers, used together:
 *
 *  1. localStorage, keyed by scan id — survives page reloads, preserves access across
 *     sessions on the same browser (e.g. visitor closes the tab, comes back via the
 *     emailed link).
 *  2. URL hash query param (?t=...) on the scan page — lets the visitor share the link
 *     with a colleague AND lets the emailed report-ready link work in any browser, not
 *     just the one that started the scan.
 *
 * The token never leaves the visitor's browser via the network except via the
 * X-Scan-Access-Token header on API calls (or the ?token= query param on PDF download —
 * required because <a download> can't attach headers).
 */

const KEY_PREFIX = 'eaa-anon-scan-token:';
const MAX_AGE_MS = 30 * 86_400_000; // 30 days. Tokens beyond that age are effectively dead.

interface StoredToken {
  token: string;
  storedAtMs: number;
}

/** Persists the access token returned by POST /api/scans/anonymous. */
export function storeAnonymousScanToken(scanId: string, token: string): void {
  try {
    const payload: StoredToken = { token, storedAtMs: Date.now() };
    localStorage.setItem(`${KEY_PREFIX}${scanId}`, JSON.stringify(payload));
  } catch {
    /* private-mode quota / disabled storage — fall back to URL-hash-only mode */
  }
}

/**
 * Reads the access token for a given scan. Returns null if never stored or older than
 * MAX_AGE_MS — callers should also check the URL hash for an inbound `?t=` param before
 * giving up.
 */
export function readAnonymousScanToken(scanId: string): string | null {
  try {
    const raw = localStorage.getItem(`${KEY_PREFIX}${scanId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredToken;
    if (!parsed?.token) return null;
    if (Date.now() - (parsed.storedAtMs ?? 0) > MAX_AGE_MS) {
      localStorage.removeItem(`${KEY_PREFIX}${scanId}`);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

/** Clears an access token (e.g. when the visitor signs up — the scan should now be owned). */
export function clearAnonymousScanToken(scanId: string): void {
  try {
    localStorage.removeItem(`${KEY_PREFIX}${scanId}`);
  } catch {
    /* swallow */
  }
}

/**
 * Reads a token from the current URL's query string. The router-aware caller passes the
 * search string; we extract `t` (short alias) or `token` (long alias). Returns null if
 * neither is present.
 */
export function readTokenFromSearch(search: string): string | null {
  try {
    const params = new URLSearchParams(search);
    const candidate = params.get('t') ?? params.get('token');
    if (!candidate) return null;
    return /^[A-Za-z0-9_-]{8,128}$/.test(candidate) ? candidate : null;
  } catch {
    return null;
  }
}
